import { z } from 'zod';
import { MessageTypeEnum, getSchemaForType } from './message-types.schema.js';

/**
 * Schema for the JSON Patch (RFC6902) operation
 */
export const JsonPatchOperationSchema = z.object({
  op: z.enum(['add', 'remove', 'replace', 'move', 'copy', 'test']),
  path: z.string(),
  value: z.any().optional(),
  from: z.string().optional(),
});

/**
 * Schema for a stream chunk in the response
 */
export const StreamChunkSchema = z.object({
  id: z.string().describe('Unique ID of this chunk'),
  done: z.boolean().describe('If true, this is the last chunk of this particular messageId'),
  delta: z.array(JsonPatchOperationSchema).describe('JSON diff in RFC6902 format'),
});

/**
 * Schema for the event body - the actual message content
 */
export const MessageBodySchema = z
  .object({
    type: MessageTypeEnum.describe('Type of the message'),
    version: z.number().positive().describe('Version of the message content type'),
    messageId: z.string().describe('ID of the message'),
    timestamp: z.number().positive().describe('Timestamp of the message'),
    checkpointId: z.string().optional().describe('ID of the checkpoint associated with this message, if any'),
    stream: z.boolean().describe('If true, this is a chunk of the stream'),
    content: z.any().optional().describe('Actual message content, exists if stream === false'),
    chunk: StreamChunkSchema.optional().describe('Stream chunk, exists if stream === true'),
  })
  .refine(
    (data) => {
      // If stream is true, chunk must be present
      if (data.stream === true && data.chunk === undefined) {
        return false;
      }
      // If stream is false, content must be present
      if (data.stream === false && data.content === undefined) {
        return false;
      }
      return true;
    },
    {
      message: "When 'stream' is true, 'chunk' must be present; when 'stream' is false, 'content' must be present",
      path: ['stream', 'chunk', 'content'],
    }
  )
  .superRefine((data, ctx) => {
    // Validate that content matches the expected schema for the given type
    if (data.stream === false && data.content !== undefined) {
      const schema = getSchemaForType(data.type);
      const validationResult = schema.safeParse(data.content);

      if (!validationResult.success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Content does not match schema for type "${data.type}"`,
          path: ['content'],
        });
      }
    }
  });

// Re-export MessageTypeEnum and related types for convenience
export { MessageTypeEnum } from './message-types.schema.js';
export type { MessageType } from './message-types.schema.js';

// Create TypeScript types based on the schemas
export type JsonPatchOperation = z.infer<typeof JsonPatchOperationSchema>;
export type StreamChunk = z.infer<typeof StreamChunkSchema>;
export type MessageBody = z.infer<typeof MessageBodySchema>;

/**
 * Validates an event body
 * @param data The data to validate
 * @returns A validation result object
 */
export function validateMessageBody(data: unknown): z.SafeParseReturnType<unknown, MessageBody> {
  return MessageBodySchema.safeParse(data);
}
